import { useMemo, useState } from 'react'
import {
  Building2,
  Calendar as CalendarIcon,
  Clock,
  Loader2,
  MessageSquare,
  Receipt,
  X,
} from 'lucide-react'
import { Calendar } from '../features/transcripts/components/Calendar'
import { CallDetailDrawer } from '../features/conversations/components/CallDetailDrawer'
import { useCalls } from '../features/conversations/hooks/useCalls'
import { LiveIndicator } from '../components/LiveIndicator'
import { AdminBanner } from '../features/admin/components/AdminBanner'
import { EmptyState } from '../features/admin/components/EmptyState'
import { useSelectedPropertyId, useSelectedPropertyType } from '../store/propertyStore'
import { formatters, formatRelative } from '../utils/formatters'
import { todayUtcISO } from '../utils/utcDay'
import type { Call } from '../types/api.types'

function durationLabel(call: Call): string {
  return formatters.formatDuration(
    call.audio_duration_ms != null ? call.audio_duration_ms / 1000 : null,
  )
}

// A call that never became an order is the one worth listening to, so this is
// not just an "ordered only" toggle — both halves are a real view.
type OrderFilter = 'ALL' | 'ORDERED' | 'NOT_ORDERED'

const ORDER_FILTERS: { value: OrderFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'ORDERED', label: 'Ordered' },
  { value: 'NOT_ORDERED', label: 'No order' },
]

function matchesFilter(call: Call, filter: OrderFilter): boolean {
  if (filter === 'ALL') return true
  return filter === 'ORDERED' ? call.order_id != null : call.order_id == null
}

export function ConversationsPage(): JSX.Element {
  const propertyId = useSelectedPropertyId()
  const propertyType = useSelectedPropertyType()

  // Undefined means "the most recent calls, whatever day they fall on". That is
  // the default on purpose: `day` buckets on UTC midnight, so a GMT+5 kitchen
  // still serving at 1am would otherwise watch its calls land on tomorrow.
  const [day, setDay] = useState<string | undefined>(undefined)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [orderFilter, setOrderFilter] = useState<OrderFilter>('ALL')
  const [selectedCall, setSelectedCall] = useState<Call | null>(null)

  const { calls, orders, loading, error, streamStatus, hasMore, loadingMore, loadMore } = useCalls(
    propertyId,
    day,
  )

  // ponytail: filtered client-side over what is loaded — the API takes no
  // "has an order" param, and Load more keeps paging the unfiltered list, so a
  // narrow filter just means paging further to fill it.
  const visible = useMemo(
    () => calls.filter((call) => matchesFilter(call, orderFilter)),
    [calls, orderFilter],
  )
  const counts = useMemo(() => {
    const ordered = calls.filter((call) => call.order_id != null).length
    return { ALL: calls.length, ORDERED: ordered, NOT_ORDERED: calls.length - ordered }
  }, [calls])

  if (propertyId == null) {
    return (
      <EmptyState
        icon={Building2}
        message="Select a property"
        hint="Pick one from the bar at the top to see its conversations."
      />
    )
  }

  if (propertyType !== 'RESTAURANT') {
    return (
      <EmptyState
        icon={MessageSquare}
        message="Select a restaurant"
        hint="Conversations belong to restaurant properties. Switch property from the bar above."
      />
    )
  }

  // The drawer is a sibling of the page stack, not a child of it: `space-y-6`
  // sets margin-top on every child after the first, and on a `fixed inset-0`
  // backdrop that margin shifts the overlay down and leaves an undimmed strip
  // across the top of the screen. Same trap the add-item modal fell into.
  return (
    <>
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[20px] font-bold text-gray-900">Conversations</h1>
          <p className="text-[13px] text-gray-500">
            What the tables said to the agent, and what it heard back.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setCalendarOpen((v) => !v)}
              type="button"
              className={`flex items-center gap-1.5 text-[13px] transition-colors ${
                calendarOpen ? 'text-brand-green' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <CalendarIcon size={14} />
              <span>{day ?? 'Latest'}</span>
            </button>
            {calendarOpen && (
              <div className="absolute right-0 top-full z-40 mt-2">
                <Calendar
                  value={day ?? todayUtcISO()}
                  onChange={setDay}
                  onClose={() => setCalendarOpen(false)}
                />
              </div>
            )}
          </div>
          {day && (
            <button
              type="button"
              onClick={() => setDay(undefined)}
              className="flex items-center gap-1 text-[13px] text-gray-400 hover:text-gray-700"
            >
              <X size={13} /> Clear
            </button>
          )}
          <LiveIndicator status={streamStatus} />
        </div>
      </div>

      <AdminBanner message={error} variant="error" />

      <div className="flex flex-wrap gap-2">
        {ORDER_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => setOrderFilter(value)}
            className={`rounded-full px-3 py-1.5 text-[13px] transition-colors ${
              orderFilter === value
                ? 'bg-brand-green font-semibold text-black'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label} <span className="opacity-60">{counts[value]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-brand-green" />
          <span className="ml-2 text-sm text-gray-500">Loading conversations...</span>
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          message={
            calls.length > 0
              ? 'Nothing in this view'
              : day
                ? 'No conversations that day'
                : 'No conversations yet'
          }
          hint={
            calls.length > 0
              ? 'Try another filter, or load more to reach older calls.'
              : day
                ? 'Days are counted in UTC, so a late night can land on the next one.'
                : 'Calls to the tabletop agent land here as they finish.'
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visible.map((call) => (
              <div
                key={call.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedCall(call)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedCall(call)
                  }
                }}
                className="flex min-w-0 cursor-pointer flex-col rounded-lg border border-gray-200 bg-white p-5 transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-green/50"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate text-[15px] font-semibold text-gray-900">
                      Table {call.table_number ?? '—'}
                    </h3>
                    <p className="text-[12px] text-gray-400">
                      #{call.id} · {formatRelative(call.started_at)}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-gray-600">
                    <Clock size={11} /> {durationLabel(call)}
                  </span>
                </div>

                <p className="mb-3 text-[13px] text-gray-500">
                  {call.message_count ?? 0} {call.message_count === 1 ? 'turn' : 'turns'}
                </p>

                <div className="mt-auto">
                  {call.order_id != null ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-[11px] font-medium text-green-700">
                      <Receipt size={12} /> Order #{call.order_id}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-500">
                      No order placed
                    </span>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Outside the empty check on purpose: a filter can hide every loaded row
          while older pages still hold matches. No "showing X of Y" either — the
          API's `total` is this page's length, not the size of the result set. */}
      {!loading && hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-5 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingMore && <Loader2 size={14} className="animate-spin" />}
            Load more
          </button>
        </div>
      )}

    </div>

      <CallDetailDrawer
        call={selectedCall}
        liveOrders={orders}
        onClose={() => setSelectedCall(null)}
      />
    </>
  )
}
